import { mapActions, mapGetters } from 'vuex'
import countries from '@/utils/_countries.json'
import languages from '@/utils/_languages.json'

export default {
  name: 'ListicleFinder',
  data () {
    return {
      isLoading: {
        listicleFinder: false,
        user: false,
        email: false
      },

      homepage: null,
      homepageRules: [
        value => !!value || 'Homepage URL is required',
        value => /^(https?:\/\/)?(www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,}\/?$/.test(value) || 'Invalid homepage URL'
      ],
      productCategory: null,
      productCategoryRules: [
        value => !!value || 'Product category is required',
        value => (value && value.length >= 3) || 'Category must be at least 3 characters'
      ],
      email: null,
      emailSent: false,
      emailSaved: false,
      emailRules: [
        value => !value || /.+@.+\..+/.test(value) || 'Invalid email'
      ],

      form: null,
      countries,
      country: 'USA',
      languages,
      language: 'en',

      contentplanId: null,
      workspaceId: null,
      step: 0,
      csvUrl: null,
      searchTermsCount: 0,
      socket: null
    }
  },
  computed: {
    ...mapGetters([
      'websocketUrl'
    ]),
    signUpURI () {
      if (!this.contentplanId) return ''
      return `/auth/in?contentplanId=${this.contentplanId}`
    },
    isRusLocation () {
      return this.country === 'RUS' || this.country === 'BLR'
    },
    isRusDomain () {
      if (!this.form) return false
      return /(ru|by)\/?$/u.test(this.homepage)
    },
    errorMessage () {
      if (this.isRusLocation) return 'We don\'t support Russia or Belarus.'
      if (this.isRusDomain) return 'We don\'t support Russian or Belarussian domains.'
      return null
    }
  },
  watch: {
  },
  methods: {
    ...mapActions([
      'postUser',
      'sendEmail'
    ]),
    async runListicleFinder () {
      try {
        if (!this.homepage || !this.productCategory) return
        this.isLoading.listicleFinder = true

        // Start websocket connection and trigger listicle finder
        await this.connectWebSocketAndTriggerListicleFinder()
      } catch (err) {
        console.log(err)
        this.isLoading.listicleFinder = false
      }
    },
    async connectWebSocketAndTriggerListicleFinder () {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.log('WebSocket connection not established, opening...')
        this.socket = new WebSocket(this.websocketUrl)
      }

      this.socket.onopen = () => {
        console.log('WebSocket connection established')
        // Trigger the listicle finder via websocket
        this.sendMessage({
          action: 'runListicle',
          message: {
            homepage: this.homepage,
            productCategory: this.productCategory,
            country: this.country,
            language: this.language,
            email: this.email
          }
        })
      }

      // Handle incoming messages
      this.socket.onmessage = (event) => {
        console.log('Message received:', event)
        this.websocketMessageHandler(event)
      }

      this.socket.onerror = (error) => {
        console.error('WebSocket error: ', error.message)
      }

      this.socket.onclose = () => {
        console.log('WebSocket connection closed')
      }
    },
    async sendMessage (body) {
      try {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection not established, opening...')
          await this.connectWebSocketAndTriggerListicleFinder()
        } else {
          console.log('WebSocket connection open, sending message...')
          this.socket.send(JSON.stringify(body))
        }
      } catch (err) {
        console.error(err)
      }
    },
    async websocketMessageHandler (event) {
      const eventData = JSON.parse(event.data)
      const messages = eventData?.messages
      if (!messages) return
      const messageData = messages[0]
      if (!messageData) return
      const { stepName, status, contentplanId, csvUrl } = messageData

      console.log('WebSocket message:', { stepName, status, contentplanId, csvUrl })

      // Capture contentplanId from first message
      if (contentplanId && !this.contentplanId) {
        this.contentplanId = contentplanId
        this.workspaceId = 1 // Free listicle finder uses workspace 1
      }

      // Handle step transitions (listicle finder goes through similar steps as clusterer)
      if (stepName === 'starting' && status === 'started') {
        // Process has started, stop loading button and show progress tracker
        this.isLoading.listicleFinder = false
        this.step = 1
      }

      if (stepName === 'Analyzing' && status === 'inProgress') {
        this.step = 3 // Crawling complete
      }

      if (stepName === 'Analyzing' && status === 'done' && csvUrl) {
        this.csvUrl = csvUrl
        this.step = 4
        this.isLoading.listicleFinder = false
      }

      // Handle errors
      if (status === 'error') {
        this.isLoading.listicleFinder = false
      }
    },
    reset () {
      this.homepage = null
      this.productCategory = null
      this.country = 'USA'
      this.language = 'en'
      this.step = null
      this.csvUrl = null
      this.contentplanId = null
      this.workspaceId = null
      this.emailSent = false
      this.emailSaved = false
      this.searchTermsCount = 0
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
    },
    async addNewUser () {
      this.isLoading.user = true

      let userId
      try {
        userId = await this.postUser({
          contentplanId: this.contentplanId,
          email: this.email
        })
      } catch (err) {
        console.log(err)
      }

      this.emailSaved = true
      this.isLoading.user = false

      return userId
    },
    async addNewUserAndSendEmail () {
      this.isLoading.email = true

      try {
        const userId = await this.addNewUser()
        await this.sendEmail({
          action: 'listicle',
          userId,
          contentplanId: this.contentplanId
        })
        this.emailSent = true
      } catch (err) {
        console.log(err)
      }

      this.isLoading.email = false
    }
  },
  async mounted () {
    this.isLoading.user = false
  },
  beforeDestroy () {
    // Clean up websocket
    if (this.socket) {
      this.socket.close()
    }
  }
}
